import React from "react";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";

// Gradient header used across sub-pages
export const PageHeader = ({ title, subtitle, onBack, right }) => (
  <div className="bg-gradient-to-r from-[#4AADDF] to-[#367ABD] text-white pt-safe px-4 pb-4 rounded-b-[24px] shadow-[0_4px_20px_rgba(74,173,223,0.25)] relative z-10">
    <div className="flex items-center min-h-[44px]">
      {onBack && (
        <button
          data-testid="page-back-button"
          onClick={onBack}
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full active:bg-white/15 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft size={26} />
        </button>
      )}
      <div className="flex-1 text-center pr-9">
        <h1 className="font-inter font-semibold text-[18px] leading-tight">{title}</h1>
        {subtitle && <div className="mt-0.5">{subtitle}</div>}
      </div>
      {right ? <div className="absolute right-4 top-safe">{right}</div> : null}
    </div>
  </div>
);

// Chat bubble
export const ChatBubble = ({ role, text, timestamp, meta }) => {
  const isGuest = role === "user";
  const bubbleStyle = isGuest
    ? "bg-[#4AADDF] text-white rounded-[18px_18px_4px_18px]"
    : role === "operator"
    ? "bg-[#1A3A5C] text-white rounded-[18px_18px_18px_4px]"
    : "bg-white text-[#1A1A2E] border border-[#E8F4FD] rounded-[18px_18px_18px_4px]";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex flex-col max-w-[82%] ${isGuest ? "self-end items-end" : "self-start items-start"}`}
    >
      {meta && <span className="text-[11px] text-[#6B7B8D] mb-1 px-1">{meta}</span>}
      <div className={`px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words shadow-sm ${bubbleStyle}`}>
        {text}
      </div>
      {timestamp && <span className="text-[11px] text-[#6B7B8D] mt-1 px-1">{timestamp}</span>}
    </motion.div>
  );
};

export const TypingIndicator = ({ label = "..." }) => (
  <div className="self-start flex items-center gap-2 bg-white border border-[#E8F4FD] rounded-[18px_18px_18px_4px] px-4 py-3 shadow-sm">
    <span className="typing-dot" />
    <span className="typing-dot" />
    <span className="typing-dot" />
    <span className="text-[12px] text-[#6B7B8D] ml-1">{label}</span>
  </div>
);

// Language toggle pill (variant: "light" on gradient, "dark" on white)
export const LangToggle = ({ lang, setLang, variant = "dark" }) => {
  const light = variant === "light";
  return (
    <div
      data-testid="lang-toggle"
      className={`inline-flex items-center rounded-full p-0.5 text-[11px] font-semibold ${
        light ? "bg-white/20" : "bg-[#F0F7FF] border border-[#E0E8F0]"
      }`}
    >
      {["id", "en"].map((l) => {
        const active = lang === l;
        return (
          <button
            key={l}
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`px-2.5 py-1 rounded-full transition-all duration-200 ${
              active
                ? light
                  ? "bg-white text-[#367ABD]"
                  : "bg-[#4AADDF] text-white"
                : light
                ? "text-white/90"
                : "text-[#6B7B8D]"
            }`}
          >
            {l.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
};

export const nowTime = () =>
  new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

export const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};
